import { resolveTargetKey } from './types';
import type { ColumnMapping, ImportRow } from './types';

/**
 * Renames a parsed row's keys from a company's configured raw HRIS headers to their mapped
 * target key (a core field like `employeeId`, or an attribute's lowercased name). A row key with
 * no configured mapping passes through unchanged, which is what preserves today's existing
 * hardcoded header-matching behavior for anything a company hasn't explicitly mapped. A mapping
 * targeting an attribute with no name for the requesting user's language is treated the same as
 * unconfigured -- there's no key to rename its column to. A mapping marked `excluded` also passes
 * its column through unchanged here -- see `dropExcludedColumns` for why it isn't dropped yet.
 *
 * Matching is case-insensitive on `sourceColumnName` only -- callers are expected to have
 * already lowercased `row`'s keys (as `convert.toLowerKeys()` already does for every existing
 * import path), so this function does not itself relowercase `row`.
 */
export function applyColumnMappings(mappings: ColumnMapping[], row: ImportRow): ImportRow {
  const targetKeyBySourceColumn = new Map<string, string>();
  for (const mapping of mappings) {
    if ('excluded' in mapping) {
      continue;
    }
    const targetKey = resolveTargetKey(mapping);
    if (targetKey !== null) {
      targetKeyBySourceColumn.set(mapping.sourceColumnName.toLowerCase(), targetKey);
    }
  }

  const mappedRow: ImportRow = {};
  for (const [key, value] of Object.entries(row)) {
    const targetKey = targetKeyBySourceColumn.get(key.toLowerCase());
    mappedRow[targetKey ?? key] = value;
  }

  return mappedRow;
}

/**
 * Drops every column marked `excluded` from a row -- e.g. a column a company's export still
 * sends but WorkTango no longer wants persisted at all. Called on the row *after* transformations
 * have run, not as part of `applyColumnMappings`: an excluded column can still be a transformation
 * input (e.g. Harris' Date of Birth feeds its Generation transformation even though the raw Date
 * of Birth value itself must never be persisted), so dropping it before transformations run would
 * silently break any transformation that reads it.
 */
export function dropExcludedColumns(mappings: ColumnMapping[], row: ImportRow): ImportRow {
  const excludedSourceColumns = new Set<string>();
  for (const mapping of mappings) {
    if ('excluded' in mapping) {
      excludedSourceColumns.add(mapping.sourceColumnName.toLowerCase());
    }
  }
  if (excludedSourceColumns.size === 0) {
    return row;
  }

  const result: ImportRow = {};
  for (const [key, value] of Object.entries(row)) {
    if (!excludedSourceColumns.has(key.toLowerCase())) {
      result[key] = value;
    }
  }
  return result;
}
